import { Box, Grid, Icon, Stack, Tooltip, Typography } from "@mui/material"
import { mapIconToWeatherCode, weatherCodeToDescription, mockWeatherDatasets } from "./sample"
import { ChevronRight, PlaceOutlined } from "@mui/icons-material"
import { map, random } from "lodash";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { ReportContext } from "@/Contexts/ReportContext";
import CurrentUserContext from "@/Contexts/CurrentUser";

const WEATHER_CARD_COLLAPSED_WIDTH = 205;
const WEATHER_FORECAST_BLOCK_WIDTH = 50;

const WeatherCard: React.FC = () => {
  const { dateTimePreference } = useContext(CurrentUserContext);
  const { selectedReport } = useContext(ReportContext);
  const [currentForecast, setCurrentForecast] = useState<typeof mockWeatherDatasets[number]>(mockWeatherDatasets[0]);
  const indexOfCurrentForecast = currentForecast?.hourly.time.indexOf(currentForecast?.current.time);
  const weatherCardExpandedWidth = WEATHER_CARD_COLLAPSED_WIDTH + (4 * WEATHER_FORECAST_BLOCK_WIDTH + 35); // last number is padding


  useEffect(() => {
    if (selectedReport) {
      setCurrentForecast(mockWeatherDatasets[random(0, mockWeatherDatasets.length - 1)])
    }
  }, [selectedReport?.reportDate])

  return (
    <Grid container alignItems='center' marginLeft={1} spacing={2}>
      {/* on-hover resizing container */}
      <Box
        data-testid="weather-card-container"
        sx={{
          position: "relative",
          width: WEATHER_CARD_COLLAPSED_WIDTH,
          overflow: "hidden",
          backgroundColor: "#8299ff",
          color: "white",
          borderRadius: 2,
          transition: "width 0.2s ease-in-out",
          "&:hover": {
            width: weatherCardExpandedWidth,
            ".chevron": {
              opacity: 0,
            },
          },
        }}
      >
        {/* default showing temperature + hidden 4 blocks list */}
        <Box
          data-testid="weather-card-blocks-list"
          sx={{
            display: "flex",
            alignItems: "center",
            width: weatherCardExpandedWidth,
            flexShrink: 0,
            paddingY: 1,
          }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexShrink: 0,
              width: WEATHER_CARD_COLLAPSED_WIDTH,
              boxSizing: "border-box",
              paddingLeft: 2,
            }}>
            <Tooltip title={weatherCodeToDescription(currentForecast.current.weather_code)}>
              <Stack alignItems="center">
                <Stack direction="row" sx={{ justifyContent: "center", alignItems: "center", flexShrink: 0 }}>
                  <Stack>
                    <Icon fontSize="medium">
                      {mapIconToWeatherCode(currentForecast.current.weather_code)}
                    </Icon>
                  </Stack>
                  <Typography variant="h5" fontWeight="bold" marginLeft={0.5}>{Math.round(currentForecast.current.apparent_temperature)}&deg;</Typography>
                </Stack>

                <Typography variant="overline" noWrap sx={{ fontSize: "0.65rem", lineHeight: 1 }}>{dayjs(currentForecast.current.time).format(dateTimePreference.time)}</Typography>
              </Stack>
            </Tooltip>

            <Box sx={{ height: "36px", borderRight: "1px solid #ffffff69", marginLeft: 1, marginRight: 2, flexShrink: 0 }} />

            <Stack sx={{ justifyContent: "center", flexShrink: 0, minWidth: 0 }} >
              <Typography variant="caption" noWrap>Feels like <span style={{ fontWeight: "bold" }}>{Math.round(currentForecast.current.temperature_2m)}&deg;</span></Typography>
              <Stack direction="row" alignItems="center" spacing={0.3}>
                <PlaceOutlined sx={{ fontSize: "14px" }} />
                <Typography variant="caption" noWrap>London</Typography>
              </Stack>
            </Stack>
          </Box>

          {/* weather/time blocks with 2 hour intervals */}
          {map(new Array(4), (_, idx) => {
            const currentIndex = indexOfCurrentForecast + (idx + 1) * 2; // skipping 2 hours between blocks

            return (
              <Stack
                key={`weather-block-${idx}`}
                sx={{
                  flexShrink: 0,
                  width: WEATHER_FORECAST_BLOCK_WIDTH,
                  minWidth: WEATHER_FORECAST_BLOCK_WIDTH,
                  alignItems: "center",
                  justifyContent: "center",
                  borderLeft: "1px solid #ffffff69",
                  paddingX: 0.5,
                }}>
                <Typography noWrap fontWeight="bold">{currentForecast.hourly.temperature_2m[currentIndex]}&deg;</Typography>
                <Typography variant="caption" noWrap sx={{ fontSize: "0.65rem" }}>{dayjs(currentForecast.hourly.time[currentIndex]).format(dateTimePreference.time)}</Typography>
              </Stack>
            )
          })}
        </Box>

        <ChevronRight
          className="chevron"
          fontSize="small"
          sx={{
            position: "absolute",
            top: "50%",
            right: 1,
            transform: "translateY(-50%)",
            opacity: 0.6,
            transition: "opacity 0.2s ease-in-out",
            pointerEvents: "none",
          }}
        />

      </Box>
    </Grid>
  )
}

export default WeatherCard
